import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const AnuragEvent = () => {
  const agenda = [
    {
      title: 'AI Project Expo',
      description: 'Student and community/startups AI project and product showcases',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      )
    },
    {
      title: 'Industry Talks',
      description: 'Experts share insights and trends',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
        </svg>
      )
    },
    {
      title: 'Hands-On Workshops',
      description: 'Build AI agents • AI Mindset and Confidence • Research paper poster presentation',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      )
    },
    {
      title: 'Networking Lounges & Demos',
      description: 'Connect with innovators and explore AI demonstrations',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>AI Day @ Anurag University — January 5, 2026 | Hyderabad</title>
        <meta name="description" content="AI Day at Anurag University, Hyderabad — A flagship AI experience with project expo, industry talks, hands-on workshops, and research paper presentations. Organized by AI Anytime." />
        <meta name="keywords" content="AI Day, Anurag University, Hyderabad, AI expo, AI workshops, AI Anytime, Kodryx AI, artificial intelligence" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="AI Day @ Anurag University — January 5, 2026" />
        <meta property="og:description" content="A flagship AI experience at Anurag University featuring project expo, industry talks, workshops, and research paper presentations." />
        <meta property="og:url" content="https://aidayevents.com/events/anurag-university" />
        <meta property="og:image" content="https://aidayevents.com/images/events/Anurag-University-Hyderabad-Im.png" />
        <meta property="og:site_name" content="AI Day Events" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AI Day @ Anurag University — January 5, 2026" />
        <meta name="twitter:description" content="A flagship AI experience at Anurag University featuring project expo, industry talks, workshops, and research paper presentations." />
        <meta name="twitter:image" content="https://aidayevents.com/images/events/Anurag-University-Hyderabad-Im.png" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-black pt-32 pb-20">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="/images/events/anurag university inner.jpg" 
            alt="Anurag University" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-crimson font-outfit font-800 text-lg mb-4 uppercase tracking-wide">
              Anurag University Edition
            </p>
            <h1 className="text-4xl md:text-6xl font-outfit font-800 text-white mb-6">
              AI Day @ Anurag University
            </h1>
            <div className="flex items-center justify-center gap-6 text-gray-300 font-outfit font-600 text-lg mb-8">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-crimson" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                5 January 2026
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-crimson" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Hyderabad
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-crimson" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                10:00 AM IST
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://forms.gle/JpM7n8VVnctan5PP8" 
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
              >
                Register Now
              </a>
              <a href="#agenda" className="btn-secondary-light">
                View Event Agenda
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About this Edition */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-outfit font-800 text-black mb-8 text-center">
              About this Edition
            </h2>
            <div className="bg-white p-8 rounded-lg border-2 border-gray-200 shadow-sm">
              <p className="text-lg font-outfit font-600 text-gray-700 leading-relaxed mb-6">
                Anurag University in collaboration with <a href="https://aianytime.net/" target="_blank" rel="noopener noreferrer" className="text-crimson font-800 hover:underline">AI Anytime</a> presents 
                a flagship AI experience — showcasing real-world AI breakthroughs, hands-on workshops, and industry insights.
              </p>
              <div className="border-l-4 border-crimson pl-6">
                <h3 className="text-xl font-outfit font-800 text-black mb-3">About Anurag University</h3>
                <p className="text-gray-700 font-outfit font-600 mb-4">
                  Leading private university in Hyderabad focusing on engineering and AI education.
                </p>
                <ul className="space-y-2 text-gray-700 font-outfit font-600">
                  <li className="flex items-start gap-2">
                    <img src="/images/icons/check-icon.svg" alt="Check" className="w-5 h-5 mt-1" />
                    <span>AI & ML programs with cutting-edge curriculum</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <img src="/images/icons/check-icon.svg" alt="Check" className="w-5 h-5 mt-1" />
                    <span>State-of-the-art research labs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <img src="/images/icons/check-icon.svg" alt="Check" className="w-5 h-5 mt-1" />
                    <span>Active industry collaboration and partnerships</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Event Coordinators */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-outfit font-800 text-black mb-12 text-center">
              Event Coordinators
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Faculty Coordinator */}
              <div className="bg-gray-50 border-2 border-gray-200 p-8 rounded-lg hover:border-crimson transition-all duration-300 shadow-sm text-center">
                <div className="w-16 h-16 bg-crimson rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <h3 className="text-xl font-outfit font-800 text-black mb-2">Faculty Coordinator</h3>
                <p className="text-lg font-outfit font-600 text-crimson">Dr. Sravath Kumar</p>
              </div>

              {/* Student Coordinators */}
              <div className="bg-gray-50 border-2 border-gray-200 p-8 rounded-lg hover:border-crimson transition-all duration-300 shadow-sm text-center">
                <div className="w-16 h-16 bg-crimson rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-outfit font-800 text-black mb-2">Student Coordinators</h3>
                <p className="text-lg font-outfit font-600 text-crimson mb-1">K. Nidhish</p>
                <p className="text-lg font-outfit font-600 text-crimson">Y.S S Vaishnavi</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Agenda & Tracks */}
      <section id="agenda" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-outfit font-800 text-black mb-4 text-center">
            Agenda & Tracks
          </h2>
          <p className="text-center text-gray-600 font-outfit font-600 mb-12">
            5 January 2026 @ Anurag University
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {agenda.map((item, index) => (
              <div 
                key={index}
                className="bg-gray-50 border-2 border-gray-200 p-8 rounded-lg hover:border-crimson transition-all duration-300 shadow-sm"
              >
                <div className="w-12 h-12 bg-crimson rounded-lg flex items-center justify-center mb-4 text-white">
                  {item.icon}
                </div>
                <h3 className="text-xl font-outfit font-800 text-black mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-600 font-outfit font-600">
                  {item.description}
                </p>
              </div>
            ))}
          </div>

          {/* Submission CTAs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto mt-12">
            {/* Research Paper Submission */}
            <div className="bg-gradient-to-r from-crimson to-black p-8 rounded-lg shadow-xl text-center">
              <h3 className="text-xl md:text-2xl font-outfit font-800 text-white mb-4">
                Submit Research Paper
              </h3>
              <p className="text-white font-outfit font-600 mb-6">
                Showcase your AI research at the poster presentation session
              </p>
              <Link 
                to="/events/anurag-university/paper-submission"
                className="btn-primary bg-white text-black hover:bg-gray-100 inline-block"
              >
                Submit Paper
              </Link>
            </div>

            {/* Project Expo Submission */}
            <div className="bg-gradient-to-r from-black to-crimson p-8 rounded-lg shadow-xl text-center">
              <h3 className="text-xl md:text-2xl font-outfit font-800 text-white mb-4">
                Submit Project for Expo
              </h3>
              <p className="text-white font-outfit font-600 mb-6">
                Display your AI project and compete for exciting rewards
              </p>
              <Link 
                to="/events/anurag-university/project-expo"
                className="btn-primary bg-white text-black hover:bg-gray-100 inline-block"
              >
                Submit Project
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Speakers & Mentors */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-outfit font-800 text-black mb-4">
              Speakers & Mentors
            </h2>
            <p className="text-lg font-outfit font-600 text-gray-600">
              Learn from industry experts and AI pioneers
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Speaker 1 */}
            <div className="bg-gray-50 border-2 border-gray-200 rounded-lg overflow-hidden hover:border-crimson transition-all duration-300 shadow-sm hover:shadow-xl">
              <div className="aspect-square overflow-hidden">
                <img 
                  src="/images/speakers/Sonu Kumar, AI Anytime & Kodryx AI.png"
                  alt="Sonu Kumar"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 text-center">
                <h3 className="text-xl font-outfit font-800 text-black mb-2">Sonu Kumar</h3>
                <p className="text-crimson font-outfit font-600 mb-1">Founder & CEO</p>
                <p className="text-gray-600 font-outfit font-600 text-sm"><a href="https://aianytime.net/" target="_blank" rel="noopener noreferrer" className="hover:underline">AI Anytime</a> & <a href="https://kodryx.ai" target="_blank" rel="noopener noreferrer" className="hover:underline">Kodryx AI</a></p>
              </div>
            </div>

            {/* Speaker 2 */}
            <div className="bg-gray-50 border-2 border-gray-200 rounded-lg overflow-hidden hover:border-crimson transition-all duration-300 shadow-sm hover:shadow-xl">
              <div className="aspect-square overflow-hidden">
                <img 
                  src="/images/speakers/Venkata Talatam, CEO, Kodryx AI.png"
                  alt="Venkata Talatam"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 text-center">
                <h3 className="text-xl font-outfit font-800 text-black mb-2">Venkata Talatam</h3>
                <p className="text-crimson font-outfit font-600 mb-1">CEO</p>
                <p className="text-gray-600 font-outfit font-600 text-sm"><a href="https://kodryx.ai" target="_blank" rel="noopener noreferrer" className="hover:underline">Kodryx AI</a></p>
              </div>
            </div>

            {/* Speaker 3 */}
            <div className="bg-gray-50 border-2 border-gray-200 rounded-lg overflow-hidden hover:border-crimson transition-all duration-300 shadow-sm hover:shadow-xl">
              <div className="aspect-square overflow-hidden">
                <img 
                  src="/images/speakers/Daisuke Tanji, Indobox Inc, CEO.png"
                  alt="Daisuke Tanji"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 text-center">
                <h3 className="text-xl font-outfit font-800 text-black mb-2">Daisuke Tanji</h3>
                <p className="text-crimson font-outfit font-600 mb-1">CEO</p>
                <p className="text-gray-600 font-outfit font-600 text-sm">Indobox Inc</p>
              </div>
            </div>

            {/* Speaker 4 */}
            <div className="bg-gray-50 border-2 border-gray-200 rounded-lg overflow-hidden hover:border-crimson transition-all duration-300 shadow-sm hover:shadow-xl">
              <div className="aspect-square overflow-hidden">
                <img 
                  src="/images/speakers/Sanathan Selvan, CEO, Unity Circle.png"
                  alt="Sanathan Selvan"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 text-center">
                <h3 className="text-xl font-outfit font-800 text-black mb-2">Sanathan Selvan</h3>
                <p className="text-crimson font-outfit font-600 mb-1">CEO</p>
                <p className="text-gray-600 font-outfit font-600 text-sm">Unity Circle</p>
              </div>
            </div>

            {/* Speaker 5 */}
            <div className="bg-gray-50 border-2 border-gray-200 rounded-lg overflow-hidden hover:border-crimson transition-all duration-300 shadow-sm hover:shadow-xl">
              <div className="aspect-square overflow-hidden">
                <img 
                  src="/images/speakers/Ekshith Kolla Founding Engineer, kodryx ai.jpeg"
                  alt="Ekshith Kolla"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 text-center">
                <h3 className="text-xl font-outfit font-800 text-black mb-2">Ekshith Kolla</h3>
                <p className="text-crimson font-outfit font-600 mb-1">Founding Engineer</p>
                <p className="text-gray-600 font-outfit font-600 text-sm"><a href="https://kodryx.ai" target="_blank" rel="noopener noreferrer" className="hover:underline">Kodryx AI</a></p>
              </div>
            </div>

            {/* Speaker 6 */}
            <div className="bg-gray-50 border-2 border-gray-200 rounded-lg overflow-hidden hover:border-crimson transition-all duration-300 shadow-sm hover:shadow-xl">
              <div className="aspect-square overflow-hidden">
                <img 
                  src="/images/speakers/Kalyan Prasad, HYDPY and PyCon Speaker.jpeg"
                  alt="Kalyan Prasad"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 text-center">
                <h3 className="text-xl font-outfit font-800 text-black mb-2">Kalyan Prasad</h3>
                <p className="text-crimson font-outfit font-600 mb-1">Lead Data & AI Scientist</p>
              </div>
            </div>

            {/* Speaker 7 */}
            <div className="bg-gray-50 border-2 border-gray-200 rounded-lg overflow-hidden hover:border-crimson transition-all duration-300 shadow-sm hover:shadow-xl">
              <div className="aspect-square overflow-hidden">
                <img 
                  src="/images/speakers/satish teapot games ceo.jpeg"
                  alt="Satish Chandra"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 text-center">
                <h3 className="text-xl font-outfit font-800 text-black mb-2">Satish Chandra</h3>
                <p className="text-crimson font-outfit font-600 mb-1">CEO</p>
                <p className="text-gray-600 font-outfit font-600 text-sm">Teapot Games</p>
              </div>
            </div>

            {/* Speaker 8 */}
            <div className="bg-gray-50 border-2 border-gray-200 rounded-lg overflow-hidden hover:border-crimson transition-all duration-300 shadow-sm hover:shadow-xl">
              <div className="aspect-square overflow-hidden">
                <img 
                  src="/images/speakers/raaj meesa.jpeg"
                  alt="Raaj Meesa"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 text-center">
                <h3 className="text-xl font-outfit font-800 text-black mb-2">Raaj Meesa</h3>
                <p className="text-crimson font-outfit font-600 mb-1">CEO, Damnpreneur</p>
                <p className="text-gray-600 font-outfit font-600 text-sm">Media Head, <a href="https://kodryx.ai" target="_blank" rel="noopener noreferrer" className="hover:underline">Kodryx AI</a></p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why AI Day */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-outfit font-800 text-white mb-12 text-center">
              Why Attend AI Day?
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="text-center">
                <div className="w-16 h-16 bg-crimson rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-outfit font-800 text-white mb-3">What it is</h3>
                <p className="text-gray-300 font-outfit font-600">
                  A full-day AI showcase where students, professionals and enthusiasts explore AI projects, 
                  participate in workshops and listen to industry voices.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-crimson rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-outfit font-800 text-white mb-3">Why it matters</h3>
                <p className="text-gray-300 font-outfit font-600">
                  Build skills, connect with innovators, and create opportunities in the AI ecosystem.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-crimson rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-outfit font-800 text-white mb-3">Who should attend</h3>
                <p className="text-gray-300 font-outfit font-600">
                  Students, researchers, developers, founders, and anyone curious about AI.
                </p>
              </div>
            </div>

            <div className="text-center">
              <a 
                href="https://forms.gle/JpM7n8VVnctan5PP8"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary text-lg px-8 py-4"
              >
                Register for AI Day
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits & Incentives */}
      <section className="py-20 bg-gradient-to-br from-black via-gray-900 to-crimson">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-outfit font-800 text-white mb-4">
              Benefits & Incentives
            </h2>
            <p className="text-lg font-outfit font-600 text-gray-300">
              Exciting rewards for top teams in Project Expo & Research Paper Presentation
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Benefit 1 */}
            <div className="bg-white/10 backdrop-blur-sm border-2 border-white/20 p-6 rounded-lg hover:border-crimson transition-all duration-300 shadow-xl">
              <div className="w-12 h-12 bg-crimson rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-outfit font-800 text-white mb-2">YouTube Feature</h3>
              <p className="text-gray-300 font-outfit font-600">
                Top 5 teams projects featured on <a href="https://aianytime.net/" target="_blank" rel="noopener noreferrer" className="text-white hover:underline">AI Anytime</a> YouTube channel and community platform
              </p>
            </div>

            {/* Benefit 2 */}
            <div className="bg-white/10 backdrop-blur-sm border-2 border-white/20 p-6 rounded-lg hover:border-crimson transition-all duration-300 shadow-xl">
              <div className="w-12 h-12 bg-crimson rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3 className="text-lg font-outfit font-800 text-white mb-2">Interview Opportunities</h3>
              <p className="text-gray-300 font-outfit font-600">
                Direct interview opportunities with participating companies
              </p>
            </div>

            {/* Benefit 3 */}
            <div className="bg-white/10 backdrop-blur-sm border-2 border-white/20 p-6 rounded-lg hover:border-crimson transition-all duration-300 shadow-xl">
              <div className="w-12 h-12 bg-crimson rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-outfit font-800 text-white mb-2">Internship Opportunities</h3>
              <p className="text-gray-300 font-outfit font-600">
                Internship opportunities with participating companies
              </p>
            </div>

            {/* Benefit 4 */}
            <div className="bg-white/10 backdrop-blur-sm border-2 border-white/20 p-6 rounded-lg hover:border-crimson transition-all duration-300 shadow-xl">
              <div className="w-12 h-12 bg-crimson rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-outfit font-800 text-white mb-2">Student Ambassador</h3>
              <p className="text-gray-300 font-outfit font-600">
                Become <a href="https://aianytime.net/" target="_blank" rel="noopener noreferrer" className="text-white hover:underline">AI Anytime</a> student ambassador and lead community initiatives
              </p>
            </div>

            {/* Benefit 5 */}
            <div className="bg-white/10 backdrop-blur-sm border-2 border-white/20 p-6 rounded-lg hover:border-crimson transition-all duration-300 shadow-xl">
              <div className="w-12 h-12 bg-crimson rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-outfit font-800 text-white mb-2">YouTube Appearance</h3>
              <p className="text-gray-300 font-outfit font-600">
                Opportunity to appear on <a href="https://aianytime.net/" target="_blank" rel="noopener noreferrer" className="text-white hover:underline">AI Anytime</a> YouTube channel
              </p>
            </div>

            {/* Benefit 6 */}
            <div className="bg-white/10 backdrop-blur-sm border-2 border-white/20 p-6 rounded-lg hover:border-crimson transition-all duration-300 shadow-xl">
              <div className="w-12 h-12 bg-crimson rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                </svg>
              </div>
              <h3 className="text-lg font-outfit font-800 text-white mb-2">Goodies & Swag</h3>
              <p className="text-gray-300 font-outfit font-600">
                Exclusive <a href="https://aianytime.net/" target="_blank" rel="noopener noreferrer" className="text-white hover:underline">AI Anytime</a> goodies and swag for participants
              </p>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center mt-12">
            <p className="text-white font-outfit font-600 text-lg mb-6">
              And many more exciting opportunities!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/events/anurag-university/paper-submission"
                className="btn-primary bg-white text-black hover:bg-gray-100"
              >
                Submit Your Paper
              </Link>
              <Link 
                to="/events/anurag-university/project-expo"
                className="btn-secondary-light"
              >
                Submit Your Project
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Partners & Collaborators */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-outfit font-800 text-black mb-4">
              Participating Companies
            </h2>
            <p className="text-lg font-outfit font-600 text-gray-600">
              Industry leaders showcasing products, innovations, and conducting interactive sessions
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center">
            <div className="bg-white border-2 border-gray-200 p-6 rounded-lg hover:border-crimson transition-all duration-300 shadow-sm hover:shadow-xl flex items-center justify-center h-32">
              <img 
                src="/images/logos/kodryx ai.jpeg" 
                alt="Kodryx AI" 
                className="max-w-full max-h-20 object-contain"
              />
            </div>
            <div className="bg-white border-2 border-gray-200 p-6 rounded-lg hover:border-crimson transition-all duration-300 shadow-sm hover:shadow-xl flex items-center justify-center h-32">
              <img 
                src="/images/logos/unity circle.webp" 
                alt="Unity Circle" 
                className="max-w-full max-h-20 object-contain"
              />
            </div>
            <div className="bg-white border-2 border-gray-200 p-6 rounded-lg hover:border-crimson transition-all duration-300 shadow-sm hover:shadow-xl flex items-center justify-center h-32">
              <img 
                src="/images/logos/teapot games.jpeg" 
                alt="Teapot Games" 
                className="max-w-full max-h-20 object-contain"
              />
            </div>
            <div className="bg-white border-2 border-gray-200 p-6 rounded-lg hover:border-crimson transition-all duration-300 shadow-sm hover:shadow-xl flex items-center justify-center h-32">
              <img 
                src="/images/logos/indobox.jpeg" 
                alt="Indobox" 
                className="max-w-full max-h-20 object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Registration & Fees */}
      <section id="register" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-outfit font-800 text-black mb-8">
              Registration
            </h2>
            <div className="bg-white border-2 border-crimson p-8 rounded-lg shadow-xl">
              <p className="text-6xl font-outfit font-800 text-crimson mb-2">₹350</p>
              <p className="text-lg font-outfit font-600 text-gray-500 mb-6">per team (up to 4 members)</p>
              <p className="text-xl font-outfit font-600 text-gray-700 mb-8">
                Open to all students, professionals, and AI enthusiasts
              </p>
              <a 
                href="https://forms.gle/JpM7n8VVnctan5PP8"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary text-lg px-8 py-4 w-full sm:w-auto inline-block"
              >
                Register Now
              </a>
              <p className="text-sm font-outfit font-600 text-gray-500 mt-6">
                Limited seats available • Registration closes on 3 January 2026
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Venue & Map */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-outfit font-800 text-black mb-12 text-center">
            Venue & Location
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className="bg-gray-50 p-8 rounded-lg border-2 border-gray-200">
                <h3 className="text-2xl font-outfit font-800 text-black mb-6">Anurag University</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-crimson flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <div>
                      <p className="font-outfit font-800 text-black">Address</p>
                      <p className="text-gray-600 font-outfit font-600">
                        Ghatkesar Rd, Venkatapur<br />
                        Hyderabad, Majarguda<br />
                        Telangana 500088
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-crimson flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="font-outfit font-800 text-black">Date & Time</p>
                      <p className="text-gray-600 font-outfit font-600">
                        Sunday, 5 January 2026<br />
                        10:00 AM IST onwards
                      </p>
                    </div>
                  </div>
                </div>
                <a 
                  href="https://share.google/Dx9XMj2QY9jaCaubP"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary mt-6 inline-block"
                >
                  View on Google Maps
                </a>
              </div>
            </div>
            <div className="h-96 rounded-lg border-2 border-gray-300 overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3806.2!2d78.6974!3d17.3850!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb99daeaeaaaab%3A0xae93b78392bafbc2!2sAnurag%20University!5e0!3m2!1sen!2sin!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Anurag University Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AnuragEvent;
